import type { BillingInterval, SelfServePlanKey } from "@giftforward/entitlements";
import AddIcon from "@mui/icons-material/Add";
import CheckIcon from "@mui/icons-material/Check";
import CloseIcon from "@mui/icons-material/Close";
import LockIcon from "@mui/icons-material/Lock";
import { useCallback, useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { GfgButton } from "@/components/ui/GfgButton";
import { DRAWER_CTA_CLASS } from "@/constants/buttons";
import {
  PLAN_DRAWER_STRINGS,
  PLAN_DRAWER_UNLOCK_FEATURE_COUNT,
  PLAN_INTERVAL_STRINGS,
} from "@/constants/Plans";
import { usePlanActions } from "@/hooks/usePlanActions";
import { usePlans } from "@/hooks/usePlans";
import { useSubscriptionCard } from "@/hooks/useSubscriptionCard";
import { formatDateLong } from "@/utils/date-format";
import { cardBrandLabel } from "@/utils/payment-helper";
import { checkoutView } from "@/utils/plan-price-helper";
import { userFacingErrorMessage } from "@/utils/userFacingError-helper";
import BillingIntervalPills from "./BillingIntervalPills";
import SubscriptionCardForm from "./SubscriptionCardForm";

/** First N concrete features of the plan from the catalogue; section dividers are not features. */
function unlockFeatures(features: readonly (string | { divider: string })[]): string[] {
  return features
    .filter((feature): feature is string => typeof feature === "string")
    .slice(0, PLAN_DRAWER_UNLOCK_FEATURE_COUNT);
}

/**
 * Transport-level failure (network / non-200). Business declines use the response
 * message. Stays local: it is not plan copy, so `constants/Plans.ts` does not own it.
 */
const GENERIC_PAYMENT_ERROR = "Something went wrong processing your payment. Please try again.";

/** Saved card with no expiry on record. Stays local for the same reason as GENERIC_PAYMENT_ERROR. */
const NO_CARD_EXPIRY = "No expiry on file";

const TITLE_ID = "plan-upgrade-drawer-title";

/** Shared "bordered card" look for the plan/payment-method/order-summary blocks below. */
const sectionCardClass = "border border-smoke rounded-card p-4";

const linkButtonClass =
  "m-0 cursor-pointer border-0 bg-transparent p-0 font-semibold text-dark-dust underline decoration-dark-dust/40 hover:decoration-dark-dust";

const closeButtonClass =
  "flex h-10 w-10 shrink-0 cursor-pointer items-center justify-center rounded-full border-0 bg-transparent p-0 text-black/[54%] transition-colors hover:bg-black/[4%] disabled:cursor-default disabled:text-black/[26%]";

/** `Expires MM/YYYY`; null when Stripe hasn't reported an expiry yet. */
function formatCardExpiry(month: number | null, year: number | null): string | null {
  if (month === null || year === null) return null;
  return `${String(month).padStart(2, "0")}/${year}`;
}

/**
 * Starter → Pro / Pro Plus upgrade drawer (DIV-7761 F2). Right slide-in on desktop/tablet,
 * bottom sheet on mobile. States: review / processing (PAY disabled) / success / inline error.
 *
 * Monthly / Annual pills when the plan is sold both ways (Pro), Annual by default; the plan block,
 * order lines, total and Pay button follow the selection. Amounts are the catalogue's offers from
 * GET /plans — what Stripe will charge — not the cards' marketing copy.
 *
 * Self-contained on purpose (GFG-239): it completes the whole purchase — entering a first card
 * included — WITHOUT navigating, so it can be mounted over the campaign builder's upsell modal as
 * well as on Account > Plan. Anything that changed the route here would throw the buyer out of the
 * builder step they were gated on, which is the bug that ticket reports.
 *
 * Business errors arrive as RESOLVED mutation values — HTTP 200 with
 * { status: "error", message } — so they render inline above PAY and the
 * drawer stays open for retry. ["tier"] invalidation on success lives inside
 * usePlanActions; nothing to duplicate here.
 *
 * Native dialog: fixed backdrop + panel, no focus trap (Tab can still leave
 * the panel) — the repo has no focus-trap primitive yet.
 */
export default function PlanUpgradeDrawer({
  plan,
  onClose,
  disabled = false,
  onUpgraded,
}: {
  /** The plan being bought; null closes the drawer. */
  plan: SelfServePlanKey | null;
  onClose: () => void;
  /** Impersonation, no TIER edit, or another plan action in flight — PAY stays inert (PRD F7). */
  disabled?: boolean;
  /** Fired once, when an upgrade RESOLVES successfully. Optional: PlanPanel does not pass it. */
  onUpgraded?: () => void;
}) {
  const { upgrade } = usePlanActions();
  const open = plan !== null;
  // The ORGANIZATION's card, not a group's. A subscription is billed to the organization's Stripe
  // Customer and Stripe refuses a PaymentMethod attached to a different one, so the group cards
  // this used to offer could never have paid for it — see SubscriptionCardForm.
  const { card, isLoading: cardLoading, refetch: refetchCard } = useSubscriptionCard(open);
  // Set when the admin has just entered a card: the confirmed SetupIntent hands back the
  // PaymentMethod id directly, so PAY need not wait for the refetch to land.
  const [justSavedCardId, setJustSavedCardId] = useState<string | null>(null);
  const [collectingCard, setCollectingCard] = useState(false);
  // Name, price and unlock list come from the catalogue, so the drawer sells exactly what the
  // plan cards list and what the server enforces.
  const { byKey } = usePlans();
  const target = plan ? byKey(plan)?.display : undefined;
  const unlocks = target ? unlockFeatures(target.features) : [];
  const offers = plan ? (byKey(plan)?.offers ?? []) : [];
  const [billingInterval, setBillingInterval] = useState<BillingInterval>("year");
  const view = checkoutView(offers, billingInterval, target);
  const { offer, charge } = view;

  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [successRenewalDate, setSuccessRenewalDate] = useState<string | null>(null);

  const isProcessing = upgrade.isPending;
  const isSuccess = successRenewalDate !== null;

  const panelRef = useRef<HTMLDivElement>(null);

  // A payment in flight can't be stranded mid-request by a backdrop click, Escape, or the X button.
  const handleClose = useCallback(() => {
    if (isProcessing) return;
    onClose();
  }, [isProcessing, onClose]);

  // Each open starts back at the review state — stale success/error never flash.
  useEffect(() => {
    if (open) {
      setErrorMessage(null);
      setSuccessRenewalDate(null);
      setJustSavedCardId(null);
      setCollectingCard(false);
      setBillingInterval("year");
    }
  }, [open]);

  useEffect(() => {
    if (!open) return;
    function handleKeyDown(event: KeyboardEvent) {
      if (event.key === "Escape") handleClose();
    }
    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [open, handleClose]);

  useEffect(() => {
    if (open) panelRef.current?.focus();
  }, [open]);

  // A Stripe PaymentMethod id (`pm_…`) on the organization's Customer — what a subscription create
  // takes. This sent our own `payment_methods` row id until the org card flow landed, which Stripe
  // rejected outright.
  const paymentMethodId = justSavedCardId ?? card?.id ?? null;

  async function handlePay() {
    if (disabled || !paymentMethodId || !plan || !offer) return;
    setErrorMessage(null);
    try {
      const result = await upgrade.mutateAsync({
        planId: plan,
        interval: offer.interval,
        paymentMethodId,
      });
      if (result.status === "error") {
        setErrorMessage(result.message);
        return;
      }
      // renewalDate comes from the mutation response, not the plan query.
      setSuccessRenewalDate(result.renewalDate);
      // Only here — not on close, not on a decline, not on a throw. "The drawer closed" and "the
      // upgrade succeeded" are different events: a buyer who opens checkout and backs out has to
      // land back where they were, not be treated as having paid.
      onUpgraded?.();
    } catch (err) {
      // The server's own message when it sent one — see apiError-helper. A declined card already
      // arrives as a 200 body; what reaches here is a REFUSAL with a reason worth reading. A dropped
      // connection or a runtime error has no reason to read, so it gets the payment copy (GFG-427).
      setErrorMessage(userFacingErrorMessage(err, GENERIC_PAYMENT_ERROR));
    }
  }

  /**
   * A card saved. Take the PaymentMethod id straight from the confirmed SetupIntent so PAY is
   * usable immediately, and refetch so a reopen shows the saved card rather than the form again.
   *
   * This replaced a deep-link to the caller's group Payment Methods panel. That link sent admins
   * to add a card that could not have paid for this: group cards are attached to the group's
   * Stripe Customer, and a subscription on the organization's Customer cannot use one.
   */
  function handleCardSaved(newPaymentMethodId: string) {
    setJustSavedCardId(newPaymentMethodId);
    setCollectingCard(false);
    void refetchCard();
  }

  if (!open) return null;

  const orderSummaryRows = [
    { label: PLAN_DRAWER_STRINGS.orderLineName(target?.badgeLabel ?? ""), value: charge ?? "—" },
    {
      label: PLAN_DRAWER_STRINGS.billingPeriodLabel,
      value: offer ? PLAN_INTERVAL_STRINGS.pill[offer.interval] : "—",
    },
  ];

  const cardExpiry = card ? formatCardExpiry(card.expMonth, card.expYear) : null;

  return createPortal(
    <>
      {/* biome-ignore lint/a11y/noStaticElementInteractions: decorative click-outside-to-close overlay; Escape (below) is the keyboard equivalent for dismissing the dialog. */}
      {/* biome-ignore lint/a11y/useKeyWithClickEvents: same as above — Escape is wired via the keydown listener effect, not on this element. */}
      <div onClick={handleClose} className="fixed inset-0 z-[1300] bg-black/50" />

      {/* Bottom sheet on mobile, right slide-in panel at md+ */}
      <div
        ref={panelRef}
        role="dialog"
        aria-modal="true"
        aria-labelledby={TITLE_ID}
        tabIndex={-1}
        className="fixed inset-x-0 bottom-0 z-[1301] flex max-h-[92vh] w-full flex-col overflow-hidden rounded-t-2xl bg-white font-lato outline-none md:inset-x-auto md:top-0 md:right-0 md:bottom-0 md:h-full md:max-h-none md:w-[580px] md:max-w-[100vw] md:rounded-none"
      >
        <div
          // Success has no header text, so the full band would be ~80px of bordered dead space
          // above "You're on <plan>": collapse it to just the close affordance.
          className={
            isSuccess
              ? "flex items-center justify-end p-2"
              : "flex items-center justify-between p-6 pb-4 border-b border-smoke"
          }
        >
          {!isSuccess && (
            <p id={TITLE_ID} className="text-[18px] font-semibold">
              {PLAN_DRAWER_STRINGS.title(target?.name ?? "")}
            </p>
          )}
          <button
            type="button"
            onClick={handleClose}
            disabled={isProcessing}
            aria-label="Close"
            className={closeButtonClass}
          >
            <CloseIcon className="text-[24px]" />
          </button>
        </div>

        <div className="flex-1 overflow-y-auto p-6">
          {isSuccess ? (
            <div className="flex flex-col gap-4">
              <p id={TITLE_ID} className="text-[20px] font-semibold">
                {PLAN_DRAWER_STRINGS.successTitlePrefix}
                <span className="font-bold">{target?.name ?? ""}</span>
              </p>
              <p className="text-[14px] text-graphite">
                {PLAN_DRAWER_STRINGS.successBodyPrefix}
                {target?.name ?? ""}
                {PLAN_DRAWER_STRINGS.successBodySuffix} {PLAN_DRAWER_STRINGS.successRenewalPrefix}
                <span className="font-bold text-rodeo-black">
                  {formatDateLong(successRenewalDate)}
                </span>
                {PLAN_DRAWER_STRINGS.successRenewalSuffix}
              </p>
              <GfgButton
                type="button"
                variant="primary"
                onClick={onClose}
                className={`${DRAWER_CTA_CLASS} ml-auto mt-2`}
              >
                {PLAN_DRAWER_STRINGS.doneButton}
              </GfgButton>
              <p className="text-[13px] text-graphite">{PLAN_DRAWER_STRINGS.receiptNote}</p>
            </div>
          ) : (
            <div className="flex flex-col gap-5">
              <BillingIntervalPills
                offers={offers}
                value={offer?.interval ?? billingInterval}
                onChange={setBillingInterval}
                disabled={isProcessing}
              />
              <div className={sectionCardClass}>
                <p className="text-[14px] font-semibold uppercase">{target?.name ?? ""}</p>
                <p className="text-[22px] font-semibold mt-1">
                  {view.amount}
                  {view.unit}
                </p>
                <p className="text-[13px] text-graphite mt-1">{view.billing}</p>
              </div>

              <div>
                <p className="text-[14px] font-semibold mb-2 uppercase">
                  {PLAN_DRAWER_STRINGS.unlockHeading}
                </p>
                <ul className="list-none m-0 p-0">
                  {unlocks.map((feature) => (
                    <li key={feature} className="flex items-start gap-2 py-1">
                      <CheckIcon className="text-[18px] text-good-sage shrink-0 mt-0.5" />
                      <p className="text-[14px]">{feature}</p>
                    </li>
                  ))}
                </ul>
              </div>

              <div>
                <p className="text-[14px] font-semibold mb-2 uppercase">
                  {PLAN_DRAWER_STRINGS.paymentMethodLabel}
                </p>
                {cardLoading ? (
                  <div
                    role="status"
                    aria-label="Loading payment methods"
                    className="h-14 rounded-card bg-wax"
                  />
                ) : card && !collectingCard ? (
                  <div className={`${sectionCardClass} flex items-center gap-3 bg-clear-dust`}>
                    <div className="min-w-10 h-[26px] shrink-0 flex items-center justify-center border border-smoke rounded-sm bg-white px-1 text-[9px] font-extrabold whitespace-nowrap">
                      {cardBrandLabel(card.brand)}
                    </div>
                    <div className="flex-1">
                      <p className="text-[13px]">•••• {card.last4 ?? "••••"}</p>
                      <p className="text-[11px] text-graphite">
                        {cardExpiry ? PLAN_DRAWER_STRINGS.expires(cardExpiry) : NO_CARD_EXPIRY}
                      </p>
                    </div>
                    <button
                      type="button"
                      onClick={() => setCollectingCard(true)}
                      disabled={isProcessing}
                      className={`${linkButtonClass} text-[13px]`}
                    >
                      {PLAN_DRAWER_STRINGS.changeCard}
                    </button>
                  </div>
                ) : collectingCard ? (
                  // Collected here rather than linked to elsewhere: the card has to belong to the
                  // ORGANIZATION's Stripe Customer, and the group Payment Methods panel this used
                  // to deep-link to saves against the group's.
                  <SubscriptionCardForm
                    onSaved={handleCardSaved}
                    onCancel={() => setCollectingCard(false)}
                    ctaClassName={DRAWER_CTA_CLASS}
                  />
                ) : justSavedCardId ? (
                  // The card saved; the refetch that will render it is still in flight. Falling
                  // through to the "add a card" CTA would tell the buyer their new card did not save.
                  <div
                    role="status"
                    aria-label="Loading payment methods"
                    className="h-14 rounded-card bg-wax"
                  />
                ) : (
                  // No card yet: an explicit CTA, not the bare Stripe form — a form whose only
                  // button starts disabled reads as a broken drawer (GFG-248).
                  <GfgButton
                    type="button"
                    variant="primary"
                    onClick={() => setCollectingCard(true)}
                    disabled={disabled || isProcessing}
                    startIcon={<AddIcon className="text-[18px]" />}
                    className={`${DRAWER_CTA_CLASS} ml-auto`}
                  >
                    {PLAN_DRAWER_STRINGS.addPaymentMethod}
                  </GfgButton>
                )}
              </div>

              <div className={sectionCardClass}>
                {orderSummaryRows.map((row) => (
                  <div key={row.label} className="flex justify-between py-1">
                    <p className="text-[13px] text-graphite">{row.label}</p>
                    <p className="text-[13px]">{row.value}</p>
                  </div>
                ))}
                <div className="flex justify-between pt-2 mt-1 border-t border-smoke">
                  <p className="text-[14px] font-bold">{PLAN_DRAWER_STRINGS.totalLabel}</p>
                  <p className="text-[14px] font-bold">{charge ?? "—"}</p>
                </div>
              </div>

              {/* Inline business error — drawer stays open, retry allowed */}
              {errorMessage && (
                <p role="alert" className="text-[#c00] text-[13px]">
                  {errorMessage}
                </p>
              )}

              <GfgButton
                type="button"
                variant="primary"
                onClick={handlePay}
                disabled={disabled || isProcessing || cardLoading || !paymentMethodId || !offer}
                startIcon={<LockIcon className="text-[20px]" />}
                className={`${DRAWER_CTA_CLASS} ml-auto`}
              >
                {isProcessing
                  ? PLAN_DRAWER_STRINGS.processing
                  : PLAN_DRAWER_STRINGS.payButton(charge ?? "")}
              </GfgButton>

              <p className="text-[12px] text-graphite text-center">
                {PLAN_DRAWER_STRINGS.poweredByPrefix}{" "}
                <span className="font-bold">{PLAN_DRAWER_STRINGS.poweredByBrand}</span>
              </p>
            </div>
          )}
        </div>
      </div>
    </>,
    document.body,
  );
}
